import { Controller } from "stimulus"
import { useTargetMutation } from "stimulus-use"

// toastr подключён глобально через sprockets (app/assets/javascripts/application.js)
// и настроен в notifier.js — импортировать пакет заново значит собрать вторую копию.
const SOUND_KEY = "notifications:sound"
const DESKTOP_KEY = "notifications:desktop"

// Реагирует на уведомления, прилетевшие через Turbo Stream (см.
// app/views/notifications/_broadcast.turbo_stream.erb): показывает тост,
// системный баннер ОС, играет короткий сигнал и держит счётчик непрочитанных
// в заголовке вкладки. Смонтирован в layout, поэтому работает на любой
// странице; тумблеры на /notifications попадают в ту же область видимости.
export default class extends Controller {
  static targets = ["signal", "soundToggle", "desktopToggle", "desktopHint", "counter"]

  connect = () => {
    useTargetMutation(this)

    // Браузеры запрещают звук до первого взаимодействия со страницей,
    // поэтому AudioContext создаём лениво — при первом клике или нажатии клавиши.
    this.audioContext = null
    this.unreadCount = 0
    this.unlockAudio = this.unlockAudio.bind(this)
    this.updateTabTitle = this.updateTabTitle.bind(this)
    document.addEventListener("click", this.unlockAudio, { once: true })
    document.addEventListener("keydown", this.unlockAudio, { once: true })
    // Turbo-навигация переписывает document.title — возвращаем счётчик после неё.
    document.addEventListener("turbo:load", this.updateTabTitle)
  }

  disconnect = () => {
    document.removeEventListener("click", this.unlockAudio)
    document.removeEventListener("keydown", this.unlockAudio)
    document.removeEventListener("turbo:load", this.updateTabTitle)
  }

  soundToggleTargetConnected = (element) => {
    element.checked = this.soundEnabled
  }

  desktopToggleTargetConnected = (element) => {
    // iOS Safari не имеет Notifications API вовсе — прячем настройку целиком.
    if (!this.desktopSupported) {
      element.closest("label").hidden = true
      return
    }

    element.checked = this.desktopEnabled && Notification.permission === "granted"

    if (Notification.permission === "denied") {
      this.showDesktopHint()
    }
  }

  // Колокольчик в шапке заменяется через turbo_stream.replace при каждом
  // уведомлении — Connected-коллбек ловит и первоначальный рендер, и замены.
  counterTargetConnected = (element) => {
    this.unreadCount = parseInt(element.dataset.unreadCount, 10) || 0
    this.updateTabTitle()
  }

  signalTargetAdded = (element) => {
    const { notificationTitle } = element.dataset

    if (notificationTitle && window.toastr) {
      window.toastr.info(notificationTitle)
    }

    if (this.soundEnabled) {
      this.playChime()
    }

    this.showDesktopNotification(element.dataset)

    // Маркер одноразовый — иначе он копится в DOM.
    element.remove()
  }

  toggleSound = (event) => {
    localStorage.setItem(SOUND_KEY, event.target.checked ? "on" : "off")

    if (event.target.checked) {
      this.unlockAudio()
      this.playChime()
    }
  }

  toggleDesktop = (event) => {
    if (!this.desktopSupported) {
      event.target.checked = false
      return
    }

    if (!event.target.checked) {
      localStorage.setItem(DESKTOP_KEY, "off")
      return
    }

    // requestPermission обязан вызываться из жеста пользователя — клик по
    // чекбоксу им и является.
    Notification.requestPermission().then((permission) => {
      if (permission === "granted") {
        localStorage.setItem(DESKTOP_KEY, "on")
        this.hideDesktopHint()
      } else {
        event.target.checked = false
        localStorage.setItem(DESKTOP_KEY, "off")

        if (permission === "denied") {
          this.showDesktopHint()
        }
      }
    })
  }

  get soundEnabled() {
    return localStorage.getItem(SOUND_KEY) !== "off"
  }

  // В отличие от звука, по умолчанию выключено: без явного клика пользователя
  // разрешение всё равно не получить.
  get desktopEnabled() {
    return localStorage.getItem(DESKTOP_KEY) === "on"
  }

  get desktopSupported() {
    return "Notification" in window
  }

  // Системный баннер показываем только когда пользователь не смотрит на
  // страницу — при активной вкладке достаточно тоста.
  showDesktopNotification({ notificationId, notificationTitle, notificationBody, notificationPath }) {
    if (!this.desktopSupported) { return }
    if (!this.desktopEnabled || Notification.permission !== "granted") { return }
    if (!document.hidden && document.hasFocus()) { return }
    if (!notificationTitle) { return }

    // tag дедуплицирует баннеры между несколькими открытыми вкладками:
    // каждая получает броадкаст, но ОС показывает один баннер.
    const banner = new Notification(notificationTitle, {
      body: notificationBody || undefined,
      icon: "/logo.png",
      tag: `mp-notification-${notificationId}`,
    })

    banner.onclick = () => {
      window.focus()
      window.location = notificationPath || "/notifications"
      banner.close()
    }
  }

  updateTabTitle() {
    const baseTitle = document.title.replace(/^\(\d+\)\s/, "")

    document.title = this.unreadCount > 0 ? `(${this.unreadCount}) ${baseTitle}` : baseTitle
  }

  showDesktopHint() {
    if (this.hasDesktopHintTarget) {
      this.desktopHintTarget.hidden = false
    }
  }

  hideDesktopHint() {
    if (this.hasDesktopHintTarget) {
      this.desktopHintTarget.hidden = true
    }
  }

  unlockAudio() {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext

    if (!AudioContextClass) { return }

    if (!this.audioContext) {
      this.audioContext = new AudioContextClass()
    }

    if (this.audioContext.state === "suspended") {
      this.audioContext.resume()
    }
  }

  // Две короткие ноты вместо аудиофайла: не нужен ассет в репозитории
  // и возня с precompile, звук тихий и не отвлекает.
  playChime() {
    this.unlockAudio()

    if (!this.audioContext || this.audioContext.state !== "running") { return }

    const now = this.audioContext.currentTime

    this.playNote(880, now, 0.12)
    this.playNote(1174.7, now + 0.11, 0.16)
  }

  playNote(frequency, startAt, duration) {
    const oscillator = this.audioContext.createOscillator()
    const gain = this.audioContext.createGain()

    oscillator.type = "sine"
    oscillator.frequency.value = frequency

    gain.gain.setValueAtTime(0.0001, startAt)
    gain.gain.exponentialRampToValueAtTime(0.08, startAt + 0.01)
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration)

    oscillator.connect(gain)
    gain.connect(this.audioContext.destination)

    oscillator.start(startAt)
    oscillator.stop(startAt + duration)
  }
}
